import React, { useState, useEffect, useMemo, useRef } from "react";
import { Button, Badge, Modal } from "../components.jsx";
import { useStore } from "../store.jsx";
import { useIgualdad } from "../../igualdad-store.jsx";
import { apiDownload, apiUpload } from "../../igualdad-api.js";
import { planificarImportacionPuestos, planificarImportacionFactores } from "../importacion.js";

/* ============================================================
   Talention — Importación desde Excel (puestos y factores)

   Paso 1: plantilla y fichero. El backend lee el fichero y devuelve filas.
   Paso 2: vista previa. El plan se recalcula contra el espacio actual en
   cada render: si el espacio se recarga (409) mientras se mira, la vista
   previa y lo que se aplica siguen siendo lo mismo.
   Confirmar: un único dispatch y el autosave de siempre. Cancelar en
   cualquier paso no deja rastro.
   ============================================================ */

const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

const CONFIGURACION = {
  puestos: {
    titulo: "Importar puestos desde Excel",
    plantilla: "plantilla-puestos-svpt.xlsx",
    formatos: ".xlsx,.xls,.csv",
    ayuda: "Solo la denominación es obligatoria. Un puesto con la misma denominación que uno existente lo actualiza.",
    planificar: (datos, espacio) => planificarImportacionPuestos(datos.puestos || [], espacio),
    accion: "IMPORT_POSITIONS",
    cuantos: (plan) => plan.crear.length + plan.actualizar.length,
    resumen: (plan) => [
      plural(plan.crear.length, "puesto nuevo", "puestos nuevos"),
      plural(plan.actualizar.length, "actualizado", "actualizados"),
      plural(plan.departamentosNuevos.length, "departamento nuevo", "departamentos nuevos"),
      plural(plan.nivelesNuevos.length, "nivel nuevo", "niveles nuevos"),
    ].join(" · "),
    hecho: (plan) => `Importación terminada: ${plural(plan.crear.length, "puesto nuevo", "puestos nuevos")} y ${plural(plan.actualizar.length, "actualizado", "actualizados")}.`,
  },
  factores: {
    titulo: "Importar factores desde Excel",
    plantilla: "plantilla-factores-svpt.xlsx",
    formatos: ".xlsx,.xls",
    ayuda: "Crea factores personalizados. Llegan desactivados y con peso 0; actívalos y dales peso después. Los factores del catálogo no se modifican.",
    planificar: (datos, espacio) => planificarImportacionFactores(datos.factores || [], datos.niveles || [], espacio),
    accion: "IMPORT_FACTORS",
    cuantos: (plan) => plan.crear.length,
    resumen: (plan) => [
      plural(plan.crear.length, "factor nuevo", "factores nuevos"),
      plural(plan.omitidos.length, "omitido", "omitidos"),
    ].join(" · "),
    hecho: (plan) => `Importación terminada: ${plural(plan.crear.length, "factor nuevo", "factores nuevos")}, desactivados hasta que les des peso.`,
  },
};

function ImportarExcelModal({ open, tipo, onClose }) {
  const { state, dispatch } = useStore();
  const { session, fireToast } = useIgualdad();
  const config = CONFIGURACION[tipo];
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [subiendo, setSubiendo] = useState(false);
  // Se remonta el input en cada intento: así elegir otra vez el mismo fichero dispara `change`.
  const [intento, setIntento] = useState(0);
  // Id de la última subida válida: una respuesta con otro id llega tarde y se descarta.
  const subidaActual = useRef(0);

  // Al abrir y al cerrar se parte de cero e invalida cualquier subida en vuelo.
  useEffect(() => {
    subidaActual.current += 1;
    setDatos(null);
    setError(null);
    setSubiendo(false);
  }, [open]);

  const plan = useMemo(() => (datos && state ? config.planificar(datos, state) : null), [datos, state, config]);

  if (!open) return null;

  const base = `/clientes/${session.clienteId}/svpt-espacio`;

  const descargar = async () => {
    setError(null);
    try {
      await apiDownload(`${base}/plantilla/${tipo}`, null, config.plantilla);
    } catch (e) {
      setError(e.message);
    }
  };

  const subir = async (fichero) => {
    if (!fichero) return;
    const id = ++subidaActual.current;
    setIntento(n => n + 1);
    setSubiendo(true);
    setError(null);
    try {
      const res = await apiUpload(`${base}/importar/${tipo}`, {}, { fichero });
      if (id === subidaActual.current) setDatos(res.data);
    } catch (e) {
      if (id === subidaActual.current) setError(e.message);
    } finally {
      if (id === subidaActual.current) setSubiendo(false);
    }
  };

  const confirmar = () => {
    dispatch({ type: config.accion, plan });
    fireToast(config.hecho(plan));
    onClose();
  };

  // Solo factores tiene dos hojas con numeración propia: sus incidencias de
  // Niveles llevan el prefijo «Niveles: » y se muestran con la hoja aparte.
  const conHoja = tipo === "factores";
  const PREFIJO_NIVELES = "Niveles: ";
  const enHoja = (inc) => (!conHoja ? inc : inc.mensaje.startsWith(PREFIJO_NIVELES)
    ? { ...inc, hoja: "Niveles", mensaje: inc.mensaje.slice(PREFIJO_NIVELES.length) }
    : { ...inc, hoja: "Factores" });

  const importable = plan != null && config.cuantos(plan) > 0;
  const incidencias = plan
    ? [
        ...plan.errores.map(e => ({ ...e, tono: "bad", tipo: "Error" })),
        ...plan.avisos.map(a => ({ ...a, tono: "warn", tipo: "Aviso" })),
      ].sort((a, b) => (a.fila ?? 0) - (b.fila ?? 0)).map(enHoja)
    : [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={config.titulo}
      width={720}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          {plan && (
            <Button variant="primary" icon="check" disabled={!importable} onClick={confirmar}>
              Confirmar importación
            </Button>
          )}
        </>
      }
    >
      <div className="stack" style={{ gap: 14 }}>
        {!plan && (
          <>
            <div className="small muted">{config.ayuda}</div>
            <div className="row" style={{ gap: 10 }}>
              <Button icon="download" onClick={descargar}>Descargar plantilla</Button>
            </div>
            <input
              key={intento}
              type="file"
              aria-label="Fichero"
              accept={config.formatos}
              disabled={subiendo}
              onChange={e => subir(e.target.files?.[0])}
            />
            {subiendo && <div className="small muted">Leyendo el fichero…</div>}
          </>
        )}

        {error && <div className="small" style={{ color: "var(--danger-700)" }}>{error}</div>}

        {plan && (
          <>
            <div className="semi">{config.resumen(plan)}</div>
            {!importable && <div className="small muted">No hay nada que importar.</div>}
            {incidencias.length > 0 && (
              <table className="t-table">
                <thead>
                  <tr><th>Fila</th>{conHoja && <th>Hoja</th>}<th>Tipo</th><th>Motivo</th></tr>
                </thead>
                <tbody>
                  {incidencias.map((inc, i) => (
                    <tr key={i}>
                      <td className="mono">{inc.fila ?? "—"}</td>
                      {conHoja && <td>{inc.hoja}</td>}
                      <td><Badge tone={inc.tono}>{inc.tipo}</Badge></td>
                      <td>{inc.mensaje}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

export { ImportarExcelModal };
